/**
 * JavaScript Tracking Tool
 * Tracks and analyzes JavaScript loaded on a web page
 * Uses Performance API to gather data, and Page Import Map to identify 1st party entrypoints
 */

/**
 * @typedef {Object} ScriptInfo
 * @property {string} name - Script filename
 * @property {string} url - Full URL of the script
 * @property {number} size - Script size in bytes
 * @property {number} transferSize - Transfer size in bytes
 * @property {number} duration - Load duration in milliseconds
 * @property {number} startTime - Start time in milliseconds
 * @property {'script'|'module'} type - Script type
 * @property {boolean} [sizeUnavailable] - True if size info is blocked by CORS
 */

/**
 * @typedef {Object} EntrypointInfo
 * @property {string} name - Entrypoint name
 * @property {string} url - Entrypoint URL
 * @property {ScriptInfo[]} internalModules - Scripts loaded by this entrypoint
 * @property {number} totalSize - Total size of all modules
 */

/**
 * @typedef {Object} ScriptGroup
 * @property {string} name - Display name (could be comma-separated entrypoint names or basePath)
 * @property {string[]} entrypoints - List of entrypoint names in this group
 * @property {string} basePath - Base URL path
 * @property {ScriptInfo[]} scripts - Scripts in this group
 * @property {number} totalSize - Total size of all scripts
 * @property {number} count - Number of scripts
 * @property {boolean} isThirdParty - True if this group represents third-party
 * code (including same-origin scripts proxied through the page's domain)
 */

/**
 * @typedef {Object} TrackingData
 * @property {ScriptInfo[]} firstParty - First-party scripts
 * @property {ScriptInfo[]} thirdParty - Third-party scripts
 * @property {EntrypointInfo[]} entrypoints - Entrypoint information
 * @property {Object.<string, ScriptGroup>} groups - Script groups by domain/path
 */

/**
 * @typedef {Object} TrackingSummary
 * @property {Array.<{name: string, count: number, size: number}>} firstParty - First-party entrypoint summary
 * @property {Array.<{name: string, count: number, size: number|undefined}>} thirdParty - Third-party domain summary
 */

// Internal resource buffer — isolates our view from external
// performance.clearResourceTimings() calls and buffer overflows.
/** @type {PerformanceResourceTiming[]} */
const resourceBuffer = [];
const seenResourceKeys = new Set();
let observerInitialized = false;

// Cache for an externally-referenced import map (<script type="importmap"
// src="...">). Populated at most once per page via warmImportMapCache() —
// see getImportMap() below for why this can't just be fetched on demand.
/** @type {Object.<string, string>|null} */
let importMapCache = null;
let importMapFetchStarted = false;

function resourceKey(entry) {
    // Dedup by URL only. Collapses preload/prefetch pairs and seed/observer
    // overlap into one entry — more accurate for "bytes delivered to this
    // page" than counting each PerformanceResourceTiming separately, which
    // would double-count when a preload and the actual load both appear.
    return entry.name;
}

function isScriptResource(entry) {
    return (
        entry.initiatorType === 'script' ||
        entry.initiatorType === 'module' ||
        entry.name.endsWith('.js') ||
        entry.name.endsWith('.mjs')
    );
}

function addResourceEntry(entry) {
    if (!isScriptResource(entry)) return;
    const key = resourceKey(entry);
    if (seenResourceKeys.has(key)) return;
    seenResourceKeys.add(key);
    resourceBuffer.push(entry);
}

/**
 * Initialize resource tracking: seed from existing entries and subscribe to
 * future ones via PerformanceObserver. Safe to call multiple times.
 *
 * Call this as early as possible in the page lifecycle so we capture entries
 * before any other code can clear the performance buffer.
 */
function init() {
    if (observerInitialized) return;
    observerInitialized = true;

    getImportMap();

    // buffered:true replays all entries currently in the performance buffer,
    // so no separate seed step is needed.
    try {
        new PerformanceObserver((list) => {
            for (const entry of list.getEntries()) {
                addResourceEntry(entry);
            }
        }).observe({ type: 'resource', buffered: true });
    } catch {
        // Observer didn't attach — buffer stays empty; tracking-lib init still completes.
    }
}

/**
 * Extract filename from a URL
 * @param {string} url - The URL to extract the filename from
 * @returns {string} The filename portion of the URL, or the full URL if parsing fails
 * @example
 * getFileName('https://example.com/path/to/script.js') // Returns 'script.js'
 */
function getFileName(url) {
    try {
        const urlObj = new URL(url);
        const pathname = urlObj.pathname;
        return pathname.substring(pathname.lastIndexOf('/') + 1) || pathname;
    } catch {
        return url;
    }
}
/**
 * Get all loaded scripts using the Performance Resource Timing API
 * Collects information about JavaScript resources including size, duration, and type.
 * Detects when size information is unavailable due to CORS restrictions.
 *
 * @returns {ScriptInfo[]} Array of script information objects
 * @remarks
 * - Uses decodedBodySize if available, otherwise falls back to encodedBodySize
 * - For cross-origin resources without Timing-Allow-Origin header, size fields are 0
 * - Filters for resources with initiatorType of 'script' or 'module', or ending in .js/.mjs
 */
function getLoadedScripts() {
    const scripts = [];
    for (const resource of resourceBuffer) {
        // Use decodedBodySize if available, otherwise fall back to encodedBodySize.
        // For cross-origin resources without Timing-Allow-Origin header, all size
        // fields are blocked (set to 0) regardless of cache status.
        const decodedSize = resource.decodedBodySize || 0;
        const encodedSize = resource.encodedBodySize || 0;
        const size = decodedSize || encodedSize;
        // If size is 0 but there was activity, size is CORS-blocked.
        const sizeUnavailable = size === 0 && resource.duration > 0;
        scripts.push({
            name: getFileName(resource.name),
            url: resource.name,
            size: size,
            transferSize: resource.transferSize || 0,
            duration: resource.duration,
            startTime: resource.startTime,
            type: resource.initiatorType === 'module' ? 'module' : 'script',
            sizeUnavailable: sizeUnavailable
        });
    }
    return scripts;
}
/**
 * Known path prefixes for scripts that are proxied through the page's own
 * domain rather than requested directly from the vendor's domain (e.g. a
 * reverse proxy that mirrors a tag-manager vendor's scripts to bypass
 * ad-blockers or CORS restrictions). Same-origin alone is not sufficient to
 * treat a script as first-party — these paths must be excluded, otherwise
 * their bytes are silently misclassified as first-party (and hidden in the
 * "<unknown>" bucket).
 *
 * We deliberately don't map these to an assumed vendor domain name (e.g.
 * "googletagmanager.com") — no network request to that domain is observable
 * once proxied, and hardcoding it would be an assumption we can't verify
 * from the resource entry alone. Instead the group is labeled with the
 * page's own hostname + this prefix (see groupScripts), which is both
 * accurate and disambiguates multiple proxied vendors from each other.
 *
 * @type {string[]}
 */
const PROXIED_THIRD_PARTY_PATHS = [
    // Google Tag Manager server-side container, proxied same-origin.
    '/mscripts/'
];

/**
 * Find the known proxy path prefix that a same-origin pathname falls under.
 * @param {string} pathname - The pathname portion of the script URL
 * @returns {string|undefined} The matching prefix, or undefined if the path
 * does not match a known proxy prefix
 */
function findProxiedThirdPartyPrefix(pathname) {
    return PROXIED_THIRD_PARTY_PATHS.find((prefix) => pathname.startsWith(prefix));
}

/**
 * Check if a script origin should be considered first-party
 * @param {string} scriptOrigin - The origin of the script to check
 * @param {string} currentOrigin - The origin of the current page
 * @param {string} [pathname] - The pathname of the script URL, used to detect
 * same-origin scripts that are actually proxied third-party vendor code
 * @param {string[]} [importMapOrigins] - Origins that serve at least one
 * import-map entrypoint actually loaded on this page (see
 * getImportMapFirstPartyOrigins)
 * @returns {boolean} True if the script is first-party, false if third-party
 * @remarks
 * - Same-origin scripts are first-party, unless their path matches a known
 *   proxied-third-party prefix (see PROXIED_THIRD_PARTY_PATHS)
 * - Cross-origin scripts are third-party, unless their origin matches one
 *   that serves a declared, loaded import-map entrypoint — trust is scoped
 *   to the whole origin (not just the specific entrypoint's directory) so
 *   sibling/shared chunks emitted elsewhere on the same dedicated CDN are
 *   still counted as first-party. The import map is authored by the page
 *   owner, so any origin it points to is an explicit first-party assertion.
 */
function isFirstParty(scriptOrigin, currentOrigin, pathname, importMapOrigins) {
    if (scriptOrigin === currentOrigin) {
        return findProxiedThirdPartyPrefix(pathname || '') === undefined;
    }
    return (importMapOrigins || []).includes(scriptOrigin);
}
/**
 * Categorize scripts into first-party and third-party arrays
 * @param {ScriptInfo[]} scripts - Array of all loaded scripts
 * @param {string} currentOrigin - The origin of the current page
 * @param {string[]} [importMapOrigins] - Origins that serve at least one
 * import-map entrypoint actually loaded on this page (see
 * getImportMapFirstPartyOrigins). Lets cross-origin, import-map-declared
 * scripts (e.g. a shared asset CDN on a different domain than the page) be
 * classified as first-party.
 * @returns {{ firstParty: ScriptInfo[], thirdParty: ScriptInfo[] }} Object containing categorized script arrays
 * @remarks
 * - Scripts are sorted by size (largest first) within each category
 * - If URL parsing fails, the script is treated as first-party
 */
function categorizeScripts(scripts, currentOrigin, importMapOrigins = []) {
    /** @type {ScriptInfo[]} */
    const firstParty = [];
    /** @type {ScriptInfo[]} */
    const thirdParty = [];
    for (const script of scripts) {
        try {
            const scriptUrl = new URL(script.url);
            const scriptOrigin = scriptUrl.origin;
            if (isFirstParty(scriptOrigin, currentOrigin, scriptUrl.pathname, importMapOrigins)) {
                firstParty.push(script);
            } else {
                thirdParty.push(script);
            }
        } catch {
            // If URL parsing fails, treat as first-party
            firstParty.push(script);
        }
    }
    // Sort by size (largest first)
    firstParty.sort((a, b) => b.size - a.size);
    thirdParty.sort((a, b) => b.size - a.size);
    return { firstParty, thirdParty };
}
/**
 * Deliberately fire-and-forget: getImportMap() must stay synchronous
 * because it's called from unload handlers where awaiting a fetch risks
 * the page being torn down before the promise resolves. This is started
 * as early as possible (see the module's `init()` bootstrap at the bottom
 * of this file) so importMapCache is populated well before those handlers
 * ever run.
 *
 * @param {string} src - The import map script's `src` URL
 */
function warmImportMapCache(src) {
    if (importMapFetchStarted) return;
    importMapFetchStarted = true;
    fetch(src)
        .then((response) => response.json())
        .then((json) => {
            importMapCache = json.imports || {};
        })
        .catch((error) => {
            console.warn('Tracking failed to fetch external import map:', error);
            importMapCache = {};
        });
}

/**
 * Get import map data from the document
 * Looks for script elements with type="systemjs-importmap" or type="importmap"
 *
 * @returns {Object.<string, string>} Object mapping entrypoint names to URLs
 * @remarks
 * - Returns empty object if no import map is found or parsing fails
 * - Only returns the "imports" section of the import map
 * - For an externally-referenced import map (`<script type="importmap"
 *   src="...">`), the content isn't available synchronously. This kicks
 *   off (or reuses) a background fetch via warmImportMapCache() and returns
 *   whatever is currently cached — {} until that fetch resolves. Callers on
 *   the unload path (see jsLoadTracker.js) rely on init() having warmed
 *   this cache long before they run.
 */
function getImportMap() {
    const importMapScript = document.querySelector('script[type="systemjs-importmap"], script[type="importmap"]');
    if (!importMapScript) {
        return importMapCache || {};
    }
    if (importMapScript.src) {
        warmImportMapCache(importMapScript.src);
        return importMapCache || {};
    }
    if (!importMapScript.textContent) {
        return {};
    }
    try {
        const importMap = JSON.parse(importMapScript.textContent);
        return importMap.imports || {};
    } catch (error) {
        console.warn('Failed to parse import map:', error);
        return {};
    }
}
/**
 * Get origins that serve at least one import-map entrypoint actually loaded
 * on this page.
 *
 * @param {Set<string>} loadedUrls - URLs of all scripts actually observed on
 * the page (via the Performance API), used to filter out import-map entries
 * that were declared but never loaded
 * @returns {string[]} Unique origins for loaded entrypoints
 */
function getImportMapFirstPartyOrigins(loadedUrls) {
    const imports = getImportMap();
    const origins = new Set();
    for (const [entrypointName, entrypointUrl] of Object.entries(imports)) {
        if (typeof entrypointUrl !== 'string') continue;
        // Skip trailing slash entries
        if (entrypointName.endsWith('/')) continue;
        const resolvedUrl = resolveUrl(entrypointUrl);
        // Only include this entrypoint if it was actually loaded
        if (!loadedUrls.has(resolvedUrl)) continue;
        try {
            origins.add(new URL(resolvedUrl).origin);
        } catch {
            // Skip if URL parsing fails
        }
    }
    return [...origins];
}
/**
 * Group scripts by common base path (directory) or domain
 * Uses entrypoint names from import map when available
 *
 * @param {ScriptInfo[]} firstParty - Array of first-party scripts
 * @param {ScriptInfo[]} thirdParty - Array of third-party scripts
 * @param {string[]} [importMapOrigins] - Origins that serve at least one
 * import-map entrypoint actually loaded on this page (see
 * getImportMapFirstPartyOrigins). Kept consistent with categorizeScripts:
 * a script that doesn't match any specific entrypoint's own directory below
 * but does match one of these origins is still grouped as first-party, not
 * swept into a third-party domain group.
 * @returns {Object.<string, ScriptGroup>} Object mapping base paths to script groups
 * @remarks
 * - Only includes entrypoints that were actually loaded
 * - Groups include all scripts matching the base path prefix
 * - Each script is assigned to exactly one group (no duplicates)
 * - Entrypoints sharing the same directory are combined into one group
 * - Third-party scripts are grouped by domain (origin)
 * - First-party scripts without entrypoints are grouped by directory
 * - Prevents duplicate entrypoint names within the same base path
 */
function groupScripts(firstParty, thirdParty, importMapOrigins = []) {
    // Null-prototype: avoids prototype pollution from URL-derived keys.
    const groups = Object.create(null);
    const allScripts = [...firstParty, ...thirdParty];
    const imports = getImportMap();
    // Read once and reused below for both entrypoint-group and standalone-group
    // classification, so a window.location access error surfaces immediately
    // rather than being silently swallowed by a URL-parse catch block.
    const currentOrigin = window.location.origin;
    // Build a set of all loaded URLs for quick lookup
    const loadedUrls = new Set(allScripts.map((s) => s.url));
    // Build a mapping from base paths (full directory) to array of entrypoint names
    const basePathToNames = Object.create(null);
    const entrypointBasePaths = [];
    for (const [entrypointName, entrypointUrl] of Object.entries(imports)) {
        if (typeof entrypointUrl !== 'string') continue;
        // Skip trailing slash entries
        if (entrypointName.endsWith('/')) continue;
        const resolvedUrl = resolveUrl(entrypointUrl);
        // Only include this entrypoint if it was actually loaded
        if (!loadedUrls.has(resolvedUrl)) {
            continue;
        }
        try {
            const urlObj = new URL(resolvedUrl);
            const entrypointBasePath = getBasePath(urlObj);
            // Map the full directory path to the entrypoint name
            if (!basePathToNames[entrypointBasePath]) {
                basePathToNames[entrypointBasePath] = [];
                entrypointBasePaths.push(entrypointBasePath);
            }
            if (!basePathToNames[entrypointBasePath].includes(entrypointName)) {
                basePathToNames[entrypointBasePath].push(entrypointName);
            }
        } catch {
            // Skip if URL parsing fails
        }
    }
    // Initialize groups for each entrypoint base path
    // Combine entrypoints that share the same directory into one group
    for (const basePath of entrypointBasePaths) {
        const entrypoints = basePathToNames[basePath];
        // Use the shortest package name for better grouping
        let name;
        if (entrypoints.length > 1) {
            // Extract package names and find the shortest one
            const packageNames = entrypoints.map((n) => getPackageName(n));
            const shortestPackage = packageNames.reduce((shortest, current) =>
                current.length < shortest.length ? current : shortest
            );
            name = `${shortestPackage}/*`;
        } else {
            name = entrypoints[0];
        }
        const basePrefix = basePath.endsWith('/') ? basePath : basePath + '/';
        groups[basePath] = {
            name,
            entrypoints,
            basePath: basePrefix,
            scripts: [],
            totalSize: 0,
            count: 0,
            isThirdParty: false
        };
    }
    // Sort basePaths by length descending so the deepest match wins. Without
    // this, a parent-dir entrypoint (e.g. /lib/web for `react`) would claim
    // every script in its subtree before more-specific basePaths get a chance.
    const sortedBasePaths = [...entrypointBasePaths].sort((a, b) => b.length - a.length);

    // Assign each script to the most-specific entrypoint base path it lives under.
    for (const script of allScripts) {
        let matched = false;
        for (const basePath of sortedBasePaths) {
            const basePrefix = basePath.endsWith('/') ? basePath : basePath + '/';
            if (script.url.startsWith(basePrefix)) {
                groups[basePath].scripts.push(script);
                groups[basePath].totalSize += script.size;
                groups[basePath].count++;
                matched = true;
                break;
            }
        }
        // If no entrypoint match, create a standalone group
        // For third-party scripts, group by domain instead of directory
        if (!matched) {
            let basePath;
            let groupName;
            // Tracked separately from basePath's origin prefix, since a
            // proxied third-party group's basePath still starts with the
            // page's own origin — string-matching basePath against
            // currentOrigin isn't reliable for classification.
            let groupIsThirdParty = false;
            try {
                const scriptUrlObj = new URL(script.url);
                const scriptOrigin = scriptUrlObj.origin;
                // Check if this is a third-party script
                const isThirdParty = !isFirstParty(
                    scriptOrigin,
                    currentOrigin,
                    scriptUrlObj.pathname,
                    importMapOrigins
                );
                groupIsThirdParty = isThirdParty;
                if (isThirdParty) {
                    const proxiedPrefix =
                        scriptOrigin === currentOrigin ? findProxiedThirdPartyPrefix(scriptUrlObj.pathname) : undefined;
                    if (proxiedPrefix) {
                        // Same-origin but proxied third-party vendor. Key by
                        // origin+prefix (not bare origin) so multiple proxied
                        // vendors don't collapse into a single group. Label
                        // with hostname+prefix rather than an assumed vendor
                        // domain — accurate to where the bytes were actually
                        // served from, and still disambiguates vendors by path.
                        basePath = scriptOrigin + proxiedPrefix;
                        groupName = scriptUrlObj.hostname + proxiedPrefix.replace(/\/$/, '');
                    } else {
                        // For third-party, group by domain (origin)
                        basePath = scriptOrigin;
                        groupName = scriptUrlObj.hostname; // Use hostname as the display name
                    }
                } else {
                    // For first-party, group by directory
                    basePath = getBasePath(scriptUrlObj);
                    groupName = basePath;
                }
            } catch {
                // Matches categorizeScripts: if URL parsing fails, treat as first-party.
                basePath = script.url;
                groupName = script.url;
                groupIsThirdParty = false;
            }
            if (!groups[basePath]) {
                groups[basePath] = {
                    name: groupName,
                    entrypoints: [],
                    basePath,
                    scripts: [],
                    totalSize: 0,
                    count: 0,
                    isThirdParty: groupIsThirdParty
                };
            }
            groups[basePath].scripts.push(script);
            groups[basePath].totalSize += script.size;
            groups[basePath].count++;
        }
    }
    return groups;
}
/**
 * Resolve a URL relative to the current page
 * @param {string} url - URL to resolve (can be relative or absolute)
 * @returns {string} Fully resolved absolute URL, or original URL if parsing fails
 * @example
 * resolveUrl('./script.js') // Returns 'https://example.com/path/script.js'
 * resolveUrl('https://cdn.com/lib.js') // Returns 'https://cdn.com/lib.js'
 */
function resolveUrl(url) {
    try {
        return new URL(url, window.location.href).href;
    } catch {
        return url;
    }
}
/**
 * Get base path for grouping (origin + directory path without filename)
 * @param {URL} urlObj - Parsed URL object
 * @returns {string} Base path consisting of origin and directory path
 * @example
 * getBasePath(new URL('https://example.com/path/to/script.js'))
 * // Returns 'https://example.com/path/to'
 */
function getBasePath(urlObj) {
    const pathParts = urlObj.pathname.split('/');
    pathParts.pop(); // Remove filename
    return urlObj.origin + pathParts.join('/');
}
/**
 * Extract npm package name from an entrypoint name
 * Supports both scoped (@org/package) and unscoped (package) package names
 *
 * @param {string} entrypointName - The entrypoint name from import map
 * @returns {string} The package name portion
 * @example
 * getPackageName('react') // Returns 'react'
 * getPackageName('react-dom') // Returns 'react-dom'
 * getPackageName('react/jsx-runtime') // Returns 'react'
 * getPackageName('react-dom/client') // Returns 'react-dom'
 * getPackageName('@vp/auth') // Returns '@vp/auth'
 * getPackageName('@vp/auth/utils') // Returns '@vp/auth'
 */
function getPackageName(entrypointName) {
    // Handle scoped packages (@org/package)
    if (entrypointName.startsWith('@')) {
        const parts = entrypointName.split('/');
        // Return @org/package (first two parts)
        return parts.slice(0, 2).join('/');
    }
    // Handle unscoped packages
    // Return everything before the first slash (or the whole string if no slash)
    const slashIndex = entrypointName.indexOf('/');
    return slashIndex === -1 ? entrypointName : entrypointName.substring(0, slashIndex);
}
/**
 * Find internal modules for an entrypoint by matching common path prefix
 * Uses the directory of the entrypoint as the base prefix for grouping related scripts
 *
 * @param {string} entrypointUrl - The URL of the entrypoint
 * @param {ScriptInfo[]} firstParty - Array of first-party scripts to search through
 * @returns {ScriptInfo[]} Array of scripts that share the same directory path as the entrypoint
 * @remarks
 * - Only includes scripts from the same directory (matching base path prefix)
 * - Returns empty array if URL parsing fails
 */
function findInternalModules(entrypointUrl, firstParty) {
    const internalModules = [];
    try {
        const entrypointUrlObj = new URL(entrypointUrl);
        const entrypointBasePath = getBasePath(entrypointUrlObj);
        const basePrefix = entrypointBasePath.endsWith('/') ? entrypointBasePath : entrypointBasePath + '/';
        for (const script of firstParty) {
            if (script.url.startsWith(basePrefix)) {
                internalModules.push(script);
            }
        }
    } catch {
        // Skip if URL parsing fails
    }
    return internalModules;
}
/**
 * Parse import map and identify entrypoints with their internal modules
 * Groups entrypoints that share the same base path into a single entry
 *
 * @param {ScriptInfo[]} firstParty - Array of first-party scripts
 * @param {ScriptInfo[]} thirdParty - Array of third-party scripts (used for loaded URL checking)
 * @returns {EntrypointInfo[]} Array of entrypoint information, sorted by total size (descending)
 * @remarks
 * - Only includes entrypoints that were actually loaded (present in Performance API)
 * - Entrypoints sharing the same directory are combined
 * - Uses shortest package name when combining multiple entrypoints
 * - Adds "/*" suffix to combined entrypoint names
 */
function analyzeImportMap(firstParty, thirdParty) {
    const imports = getImportMap();
    const allScripts = [...firstParty, ...thirdParty];
    // Build a set of all loaded URLs for quick lookup
    const loadedUrls = new Set(allScripts.map((s) => s.url));
    // Group entrypoints by their base path
    // Null-prototype: keys are derived from URLs, see groupScripts() above.
    const basePathToEntrypoints = Object.create(null);
    // First pass: collect entrypoints by base path
    for (const [entrypointName, entrypointUrl] of Object.entries(imports)) {
        if (typeof entrypointUrl !== 'string') continue;
        // Skip trailing slash entries
        if (entrypointName.endsWith('/')) continue;
        const resolvedUrl = resolveUrl(entrypointUrl);
        // Only include this entrypoint if it was actually loaded
        if (!loadedUrls.has(resolvedUrl)) {
            continue;
        }
        try {
            const urlObj = new URL(resolvedUrl);
            const basePath = getBasePath(urlObj);
            if (!basePathToEntrypoints[basePath]) {
                basePathToEntrypoints[basePath] = { names: [], urls: [] };
            }
            basePathToEntrypoints[basePath].names.push(entrypointName);
            basePathToEntrypoints[basePath].urls.push(resolvedUrl);
        } catch {
            // Skip if URL parsing fails
        }
    }
    // Second pass: collapse nested basePaths into their topmost ancestor.
    // When one entrypoint's basePath is a strict ancestor of another's
    // (e.g. ubik-react ships `react` flat at /dist/web/ while `react-dom/client`
    // lives at /dist/web/react-dom/), we treat the whole subtree as one
    // shipping unit. Without this, the parent's findInternalModules and the
    // child's both claim the same scripts, double-counting bytes.
    const allBasePaths = Object.keys(basePathToEntrypoints);
    const topmostBasePaths = allBasePaths.filter((bp) => {
        const ancestorPrefixes = allBasePaths
            .filter((other) => other !== bp)
            .map((other) => (other.endsWith('/') ? other : other + '/'));
        return !ancestorPrefixes.some((prefix) => bp.startsWith(prefix));
    });

    // Merge each non-topmost basePath's names/urls into its topmost ancestor.
    // Null-prototype: keys are derived from URLs, see groupScripts() above.
    const collapsed = Object.create(null);
    for (const top of topmostBasePaths) {
        collapsed[top] = { names: [], urls: [] };
    }
    for (const [bp, data] of Object.entries(basePathToEntrypoints)) {
        const top = topmostBasePaths.find((t) => {
            const tPrefix = t.endsWith('/') ? t : t + '/';
            return bp === t || bp.startsWith(tPrefix);
        });
        collapsed[top].names.push(...data.names);
        collapsed[top].urls.push(...data.urls);
    }

    const entrypoints = [];
    for (const [basePath, data] of Object.entries(collapsed)) {
        // Use the shortest package name as the group label when entries collapse together.
        let name;
        if (data.names.length > 1) {
            const packageNames = data.names.map((n) => getPackageName(n));
            const shortestPackage = packageNames.reduce((shortest, current) =>
                current.length < shortest.length ? current : shortest
            );
            name = `${shortestPackage}/*`;
        } else {
            name = data.names[0];
        }
        // findInternalModules grabs every script under the topmost basePath's
        // subtree — exactly what we want now that descendants are merged in.
        const internalModules = findInternalModules(data.urls[0], firstParty);
        entrypoints.push({
            name,
            url: basePath,
            internalModules,
            totalSize: internalModules.reduce((sum, m) => sum + m.size, 0)
        });
    }
    // Sort entrypoints by total size
    entrypoints.sort((a, b) => b.totalSize - a.totalSize);
    return entrypoints;
}
/**
 * Main data collection method to analyze all script resources on the page
 * Gathers performance data, categorizes scripts, identifies entrypoints, and creates script groups
 *
 * @returns {TrackingData} Complete tracking data including categorized scripts, entrypoints, and groups
 * @remarks
 * This is the primary data collection function that orchestrates all analysis steps:
 * 1. Collects all loaded scripts from Performance API
 * 2. Categorizes scripts as first-party or third-party
 * 3. Groups scripts by base path or domain
 * 4. Identifies entrypoints from import map and their internal modules
 */
function collect() {
    const currentOrigin = window.location.origin;
    const scripts = getLoadedScripts();
    const loadedUrls = new Set(scripts.map((s) => s.url));
    const importMapOrigins = getImportMapFirstPartyOrigins(loadedUrls);
    const { firstParty, thirdParty } = categorizeScripts(scripts, currentOrigin, importMapOrigins);
    const groups = groupScripts(firstParty, thirdParty, importMapOrigins);
    const entrypoints = analyzeImportMap(firstParty, thirdParty);
    return {
        firstParty,
        thirdParty,
        entrypoints,
        groups
    };
}
/**
 * Generate a tracking summary with aggregated first-party entrypoints and third-party domains
 *
 * @returns {TrackingSummary} Summarized tracking data with counts and sizes
 * @remarks
 * This is the default export function that provides a simplified view of script data:
 * - First-party scripts are grouped by entrypoint name with total counts and sizes
 * - Unaccounted first-party scripts are grouped under "<unknown>"
 * - Third-party scripts are grouped by domain with counts and sizes (or undefined if CORS-blocked)
 * - Results are sorted by size (descending)
 *
 * @example
 * const summary = generateTrackingSummary();
 * console.log('First-party:', summary.firstParty);
 * console.log('Third-party:', summary.thirdParty);
 */
function generateTrackingSummary() {
    const data = collect();
    // Map first-party entrypoints
    const firstPartyEntrypoints = data.entrypoints.map((entrypoint) => {
        return {
            name: entrypoint.name,
            count: entrypoint.internalModules.length,
            size: entrypoint.totalSize
        };
    });
    // Track which first-party scripts are accounted for in entrypoints
    const accountedScripts = new Set();
    data.entrypoints.forEach((entrypoint) => {
        entrypoint.internalModules.forEach((module) => {
            accountedScripts.add(module.url);
        });
    });
    // Find first-party scripts not in any entrypoint
    const unknownFirstParty = data.firstParty.filter((script) => !accountedScripts.has(script.url));
    // Add <unknown> group if there are unaccounted first-party scripts
    if (unknownFirstParty.length > 0) {
        const totalSize = unknownFirstParty.reduce((sum, script) => sum + script.size, 0);
        firstPartyEntrypoints.push({
            name: '<unknown>',
            count: unknownFirstParty.length,
            size: totalSize
        });
    }
    // Map third-party domains from groups
    const thirdPartyDomains = [];
    Object.values(data.groups).forEach((group) => {
        // Use the group's explicit flag rather than basePath.startsWith(currentOrigin) —
        // proxied third-party groups (see PROXIED_THIRD_PARTY_PATHS) still have a
        // basePath under the page's own origin, so a string prefix check would
        // misclassify them back to first-party here.
        if (group.isThirdParty) {
            // Count scripts with known sizes
            const modulesWithSize = group.scripts.filter((s) => s.size > 0).length;
            thirdPartyDomains.push({
                name: group.name,
                count: group.count,
                size: modulesWithSize > 0 ? group.totalSize : undefined
            });
        }
    });
    // Sort by size descending (treat undefined sizes as 0)
    thirdPartyDomains.sort((a, b) => (b.size || 0) - (a.size || 0));
    return {
        firstParty: firstPartyEntrypoints,
        thirdParty: thirdPartyDomains,
        firstPartyTotal: {
            count: firstPartyEntrypoints.reduce((sum, entry) => sum + entry.count, 0),
            size: firstPartyEntrypoints.reduce((sum, entry) => sum + entry.size, 0)
        },
        thirdPartyTotal: {
            count: thirdPartyDomains.reduce((sum, entry) => sum + entry.count, 0),
            size: thirdPartyDomains.reduce((sum, domain) => sum + (domain.size || 0), 0)
        }
    };
}

module.exports = {
    summary: generateTrackingSummary,
    collect,
    init
};

// Start observing at module load in real browsers. This is strictly earlier than
// any caller-driven init() — fewer ms during which a third-party could clear the
// performance buffer. Guarded so Node/SSR and non-browser test environments
// don't hit undefined globals at require() time.
if (typeof window !== 'undefined' && typeof PerformanceObserver !== 'undefined') {
    init();
}
